<template>
  <div class="dateTimePicker">
    <div class="flex flex-wrap">
      <div class="flex flex-wrap w-full pr-4 mb-4 input lg:w-1/2">
        <div class="input__label">
          <div class="text-xs leading-relaxed uppercase lg:text-sm font-700">
            {{ $t(fieldDateLabel) }}:
          </div>
        </div>

        <div class="input__field input__field--date">
          <v-date-picker
            v-model="currentDate"
            :popover="{ placement: 'bottom', visibility: 'click' }"
            :min-date="minDate"
            :locale="datePickerLocale"
          >
            <template v-slot="{ inputEvents }">
              <div v-on="inputEvents">
                <div
                  v-if="!currentDate"
                  class="reservation__input-value reservation__input-value--placeholder gray"
                >
                  {{ FormattedInitializationDate }}
                </div>
                <div
                  v-if="currentDate"
                  class="reservation__input-value reservation__input-value--value"
                >
                  {{ FormattedDate }}
                </div>
              </div>
            </template>
          </v-date-picker>
          <span
            class="inline-block w-5 h-5 fill-current icon text-brand-1 icon--calendar-light"
          >
            <svg
              aria-hidden="true"
              focusable="false"
              data-prefix="fal"
              data-icon="calendar-alt"
              class="svg-inline--fa fa-calendar-alt fa-w-14"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
            >
              <path
                fill="currentColor"
                d="M400 64h-48V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H128V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM48 96h352c8.8 0 16 7.2 16 16v48H32v-48c0-8.8 7.2-16 16-16zm352 384H48c-8.8 0-16-7.2-16-16V192h384v272c0 8.8-7.2 16-16 16zM148 320h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm96 0h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm96 0h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm-96 96h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm-96 0h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12zm192 0h-40c-6.6 0-12-5.4-12-12v-40c0-6.6 5.4-12 12-12h40c6.6 0 12 5.4 12 12v40c0 6.6-5.4 12-12 12z"
              ></path>
            </svg>
          </span>
          <div class="required-label" v-if="!currentDate || currentDate === ''">
            {{ $t("validation.requiredLabel") }}
          </div>
        </div>
      </div>

      <template v-if="type != 'Dive'">
        <div class="flex flex-wrap w-full pr-4 mb-4 input lg:w-1/2">
          <div class="input__label">
            <div class="text-xs leading-relaxed uppercase lg:text-sm font-700">
              {{ $t(fieldTimeLabel) }}:
            </div>
          </div>
          <div class="input__field input__field--time">

            <input type="time" placeholder="12:00" class="block pr-2 lg:hidden"
              v-model="currentTime">

            <div class="hidden lg:block">
              <flat-pickr
                v-model="currentTime"
                :config="flatPickrConfigTime"
              ></flat-pickr>
              <span
                class="inline-block w-5 h-5 fill-current icon text-brand-1 icon--clock-light"
              >
                <svg
                  aria-hidden="true"
                  focusable="false"
                  data-prefix="fal"
                  data-icon="clock"
                  class="svg-inline--fa fa-clock fa-w-16"
                  role="img"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 512 512"
                >
                  <path
                    fill="currentColor"
                    d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm216 248c0 118.7-96.1 216-216 216-118.7 0-216-96.1-216-216 0-118.7 96.1-216 216-216 118.7 0 216 96.1 216 216zm-148.9 88.3l-81.2-59c-3.1-2.3-4.9-5.9-4.9-9.7V116c0-6.6 5.4-12 12-12h14c6.6 0 12 5.4 12 12v146.3l70.5 51.3c5.4 3.9 6.5 11.4 2.6 16.8l-8.2 11.3c-3.9 5.3-11.4 6.5-16.8 2.6z"
                  ></path>
                </svg>
              </span>
            </div>
            <div class="required-label" v-if="!currentTime || currentTime === ''">
              {{ $t("validation.requiredLabel") }}
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import VCalendar from "v-calendar";
import DateTimeHelper from "@/mixins/DateTimeHelper";
import { mapGetters } from 'vuex'

import flatPickr from "vue-flatpickr-component";
import { flatPickrEnglish } from "flatpickr/dist/l10n/default.js";
import { flatPickrDutch } from "flatpickr/dist/l10n/nl.js";
import { flatPickrGerman } from "flatpickr/dist/l10n/de.js";
switch (document.querySelector("html").attributes["lang"].value) {
  case "nl-NL":
    flatpickr.localize(flatPickrDutch);
    break;
  case "de":
    flatpickr.localize(flatPickrGerman);
    break;
  default:
    flatpickr.localize(flatPickrEnglish);
    break;
}

// Use v-calendar & v-date-picker components
Vue.use(VCalendar);

export default {
  name: "DateTimePicker",
  mixins: [DateTimeHelper],
  props: {
    fieldLabel: String,
    fieldDateLabel: String,
    fieldTimeLabel: String,
    initialDateTime: String,
    currentDateTime: String,
    currDate: String,
    currTime: String,
    minDate: Date,
  },
  components: {
    flatPickr
  },
  data: function() {
    return {
      date: null,
      currentDate: null,
      currentTime: null,
      datePickerLocale: "en",
      flatPickrConfigTime: {
        altFormat: "G:i K",
        altInput: true,
        dateFormat: "H:i",
        enableTime: true,
        noCalendar: true,
        minuteIncrement: 15,
        disableMobile: true,
        time_24hr: false,
      }
    };
  },
  created() {
    this.initializeDateTimePicker();
  },
  computed: {
    ...mapGetters([
      'type'
    ]),
    FormattedDate: function() {
      return this.getLocaleFormattedDate(this.currentDate);
    },
    FormattedInitializationDate: function() {
      if (this.minDate) {
        return this.getLocaleFormattedDate(this.minDate);
      }
      return this.getLocaleFormattedDate(this.minReservationDate());
    }
  },
  watch: {
    currDate(val) {
      this.initializeCurrentDate(val);
    },
    currentDate: function(val) {
      if ( val ) {
        if (this.currentTime) {
          this.$emit("changeDateTime", { date: val, time: this.currentTime })
        }
      }
    },
    currentTime: function(val) {
      if ( val ) {
        if (this.currentDate) {
          this.$emit("changeDateTime", { date: this.currentDate, time: val })
        }
      }
    }
  },
  methods: {
    initializeDateTimePicker: function() {
      switch (document.querySelector("html").attributes["lang"].value) {
        case "nl-NL":
          this.flatPickrConfigTime.altFormat = "H:i";
          this.flatPickrConfigTime.time_24hr = true;
          this.datePickerLocale = "nl";
          break;
        case "de":
          this.flatPickrConfigTime.altFormat = "H:i";
          this.flatPickrConfigTime.time_24hr = true;
          this.datePickerLocale = "de";
          break;
        default:
          this.flatPickrConfigTime.altFormat = "G:i K";
          this.flatPickrConfigTime.time_24hr = false;
          this.datePickerLocale = "en";
          break;
      }
      this.initializeCurrentDate(this.currDate);
    },

    initializeCurrentDate: function(val) {
      if (val) {
        this.currentDate = this.getDateAsJSDateObject(val);
        if (this.currTime) {
          this.currentTime = this.currTime;
        }
      } else if (this.initialDateTime) {
        this.date = this.initialDateTime;
      }
    }
  }
};
</script>

<style scoped>
  .gray {
    color: gray;
  }
</style>
